import 'datatables.net-bs4/css/dataTables.bootstrap4.min.css';
import React, { useEffect, useState } from 'react';
import { Col, FormFeedback, FormGroup, Modal, ModalBody, ModalHeader, Row } from 'reactstrap';
import { authHeader } from '../../helpers/auth-header';
import { checkEmailIsValid, detectCardType, getContactID, getResourceString, getSupplierID, handleRequiredCheck, handleRequiredCheckAlt, isValidLuhn, StartLloydsProcess, StartOgoneProcess } from '../../helpers/general';
import Select2 from '../controls/Select2';
import Translation from '../controls/Translation';
import CardTypeIndicator from './CardTypeIndicator';

const $ = require('jquery');

const PaymentDetails_Modal = (props) => {
    let { isOpen, toggle, countries, priceExcVat = 0, upgradePackageID = 0, basketItems = {}, setModalSelect } = props;
    const [user, setUser] = useState({ supplierID: -1, contactID: -1 });
    const [paymentGateway, setPaymentGateway] = useState({ gateway:'', supportedCards:[]});
    const [detectedCardType, setDetectedCardType] = useState('Unknown');
    const [cardError, setCardError] = useState('');

    const [paymentDetails, setPaymentDetails] = useState({
        billAddrCity: '',
        billAddrCountry: '',
        billAddrLine1: '',
        billAddrLine2: '',
        billAddrPostCode: '',
        companyEmail: '',
        cardholderName: '',
        cardNumber: '',
        cardExpiryDateMM: '',
        cardExpiryDateYY: '',
        cardExpiryDate: '',
        cvc: '',
        browserColorDepth: window.screen.colorDepth,
        browserJavaEnabled: navigator.javaEnabled(),
        browserLanguage: navigator.language,
        browserScreenHeight: window.screen.height,
        browserScreenWidth: window.screen.width,
        browserTimeZone: new Date().getTimezoneOffset(),
        browserUserAgent: navigator.userAgent
    });

    useEffect(() => {
        setUser(prev => ({
            ...prev,
            supplierID: getSupplierID(),
            contactID: getContactID()
        }));

        fetch(`./Payment/GetPaymentGatewaySettings`, {
            method: 'GET',
            headers: authHeader(false, true)
        })
            .then(response => response.json())
            .then(data => {
                // map objects to just the name
                setPaymentGateway({ gateway: data.paymentGateway, supportedCards: data.gatewaySupportedCards.map(obj => obj.cardKey) });
            })
            .catch(error => console.log('Error: ', error));
    }, [])

    useEffect(() => {
        if(user.supplierID > 0)
            fetch(`./Suppliers/GetSupplierAddressDetails?supplierID=${user.supplierID}`, {
                method: 'GET',
                headers: authHeader(false, false)
            })
                .then(response => response.json())
                .then(data => {
                    if (data) {
                        setPaymentDetails(prevState => ({
                            ...prevState,
                            billAddrCity: data.city,
                            billAddrCountry: data.country,
                            billAddrLine1: data.building,
                            billAddrLine2: data.street,
                            billAddrPostCode: data.postalCode,
                            companyEmail: data.email
                        }));
                    }
                })
    }, [user.supplierID]);

    function handleChange(e) {
        e.persist();
        setPaymentDetails(prevState => ({
            ...prevState,
            [e.target.name]: e.target.value
        }));
    }

    const handleSelect2Change = e => {
        e.persist();
        setPaymentDetails(prevState => ({
            ...prevState,
            [e.target.id]: e.target.value
        }));

        if (e.target.id === 'billAddrCountry') {
            var selectedValue = e.target.value === '-1' ? '' : e.target.value;
            handleRequiredCheckAlt(e.target.id, selectedValue, 'VALIDATION_BillAddrCountry');
        }
    }

    const handleMaxSizeLimit = (e, i) => {
        if (e.target.value.length > i) {
            e.target.value = e.target.value.substring(0, i);
            return false;
        }
        return true;
    }

    const handleBetweenSizeLimit = (e, min, max) => {
        // handle Min check
        if (e.target.value.length < min) {
            return false;
        }

        // Handle Max check
        if (e.target.value.length > max) {
            e.target.value = e.target.value.substring(0, max);
            return false;
        }
        return true;
    }

    const handleCardholderNameSize = (e, min, max) => {
        if (handleBetweenSizeLimit(e, min, max)) {
            handleChange(e);
            document.getElementById(e.target.id).classList.remove("is-invalid");
            return true;
        } else {
            document.getElementById(e.target.id).classList.add("is-invalid");
            getResourceString('VALIDATION_CardHolderMinMax').then(data => {
                document.getElementById(`${e.target.id}_LABEL`).innerText = data;
            })
            return false;
        }
    }

    const handleCardNumberValidation = e => {
        let value = e.target.value;

        // Only allow numeric input
        if (isNaN(value)) {
            e.target.value = value.substring(0, value.length - 1);
            return false;
        }

        // Enforce max length (example 19 for Maestro, 16 for Visa/MC)
        if (!handleMaxSizeLimit(e, 19)) return;

        // Update state
        handleChange(e);

        // Detect card type
        const type = detectCardType(value);
        setDetectedCardType(type);

        // Check supported cards
        const supportedCardsList = ['Visa', 'Visa Debit', 'Electron', 'Mastercard', 'Maestro', 'Diners'];
        if (!supportedCardsList.includes(type) && value.length > 0) {
            setCardError('This card type is not supported');
            return;
        }

        // Check length based on card type
        const cardLengths = {
            Visa: [13, 16, 19],
            'Visa Debit': [16],
            Electron: [16],
            Mastercard: [16],
            Maestro: [12, 13, 14, 15, 16, 17, 18, 19],
            Diners: [14]
        };

        const digits = value.replace(/\D/g, '');
        const validLength = cardLengths[type]?.includes(digits.length);

        if (!validLength && digits.length > 0) {
            setCardError(`Invalid length for ${type}`);
            return;
        }

        // Luhn check
        if (digits.length >= 12 && !isValidLuhn(digits)) {
            setCardError('Invalid card number');
            return;
        }

        // Clear error if all OK
        setCardError('');
    };

    const handleMonthValidation = e => {
        if (isNaN(e.target.value)) {
            e.target.value = e.target.value.substring(0, e.target.value.length - 1)
            return false;
        }

        handleMaxSizeLimit(e, 2)

        if (e.target.value > 0 && e.target.value < 13) {
            if (handleRequiredCheck(e)) {
                handleChange(e);
            }
        } else if (e.target.value > 12) {
            document.getElementById(`${e.target.id}`).value = 12;
        } else if (e.target.value < 0 || e.target.value === '00') {
            document.getElementById(`${e.target.id}`).value = '01';
        }
    }

    const handleYearValidation = e => {
        if (isNaN(e.target.value)) {
            e.target.value = e.target.value.substring(0, e.target.value.length - 1)
            return false;
        }

        if (e.target.value.length > 2) {
            e.target.value = e.target.value.substring(0, 2);
            return false;
        }
        else {
            if (handleRequiredCheck(e)) {
                handleChange(e);
            }
        }
    }

    const handleCVC = e => {
        if (isNaN(e.target.value)) {
            e.target.value = e.target.value.substring(0, e.target.value.length - 1)
            return false;
        }

        handleMaxSizeLimit(e, 3);
        handleChange(e);
    }

    const handleMakePayment = () => {
        let bProceedPayment = true;

        // Check if there are any in-valid style errors
        $('.is-invalid').each(function (o, e) {
            bProceedPayment = false;
        });

        // Check all required fields
        $("input[required]").each(function (i, obj) {
            if ($(this).val() === '' && !$(this).hasClass('is-invalid')) {
                let fieldCheck = false;

                if (this.type === 'email') {
                    checkEmailIsValid($(this).attr('id'), $(this).val(), 'VALIDATION_default').then(vData => fieldCheck = vData);
                } else {
                    fieldCheck = handleRequiredCheckAlt($(this).attr('id'), $(this).val(), 'VALIDATION_default');
                }

                bProceedPayment = bProceedPayment && fieldCheck;
            }
        });

        // If all mandatory fields have been filled in
        if (bProceedPayment) {
            let data = [];

            Object.entries(paymentDetails).forEach(ctrl => {
                let name = ctrl[0];
                let value = ctrl[1];

                if (value !== undefined && value !== null) {
                    data.push({
                        field: name,
                        value: value.value ? parseInt(value.value) : value
                    });
                }
            });

            data.push({ field: 'priceExcVat', value: priceExcVat });
            data.push({ field: 'basketItems', value: JSON.stringify(basketItems) });

            if (paymentGateway.gateway.toUpperCase() === 'WORLDLINE') {
                StartOgoneProcess(data, user.supplierID, user.contactID, "00000000-0000-0000-0000-000000000000", upgradePackageID);
            } else if (paymentGateway.gateway.toUpperCase() === 'LLOYDS') {
                StartLloydsProcess(data, user.supplierID, user.contactID, "00000000-0000-0000-0000-000000000000", upgradePackageID);
            }
        }
    }

    const handleClose = () => {
        if (setModalSelect !== undefined) {
            setModalSelect(0);
        }
        toggle();
    }

    return (
        <Modal
            id='PaymentDetails_Modal'
            isOpen={isOpen}
            toggle={toggle}
            size="xl"
            centered
            backdrop="static"
            unmountOnClose
        >
            <ModalHeader toggle={handleClose}></ModalHeader>
            <ModalBody>
                <div className="padding-l-25 padding-r-20 card main-content__inner">
                    <div style={{ padding: "2rem" }}>
                        <h2 className="c-broken-underline u-component-spacing" style={{ width: "100%" }}>
                            Payment Details
                        </h2>
                    </div>
                    <div className="">
                        <div className="card-body">
                            <div className="c-card card u-component-spacing">
                                <div className="card-body">
                                    <div className="c-form">
                                        <Row>
                                            <Col md={6}>
                                                <h2>Billing Address Details</h2>

                                                <div className='c-form__input-group u-component-spacing--2x'>
                                                    <Translation ID="BillAddrLine1" required htmlFor='billAddrLine1' />
                                                    <FormGroup className="position-relative">
                                                        <input name="billAddrLine1" id="billAddrLine1" type="text" defaultValue={paymentDetails.billAddrLine1} className='c-form__input' required
                                                            onChange={(e) => handleChange(e)}
                                                            onBlur={(e) => handleRequiredCheck(e, 'VALIDATION_MandatoryBillAddrLine1')}
                                                        />
                                                        <FormFeedback>
                                                            <span id="billAddrLine1_LABEL"></span>
                                                        </FormFeedback>
                                                    </FormGroup>
                                                </div>

                                                <div className='c-form__input-group u-component-spacing'>
                                                    <Translation ID="BillAddrLine2" htmlFor='billAddrLine2' />
                                                    <FormGroup className="position-relative">
                                                        <input name="billAddrLine2" id="billAddrLine2" type="text" defaultValue={paymentDetails.billAddrLine2} className='c-form__input'
                                                            onChange={(e) => handleChange(e)}
                                                        />
                                                        <FormFeedback>
                                                            <span id="billAddrLine2_LABEL"></span>
                                                        </FormFeedback>
                                                    </FormGroup>
                                                </div>

                                                <div className="c-form__input-group u-component-spacing">
                                                    <Translation ID="BillAddrCity" required htmlFor='billAddrCity' />
                                                    <FormGroup className="position-relative">
                                                        <input name="billAddrCity" id="billAddrCity" type="text" defaultValue={paymentDetails.billAddrCity} className='c-form__input' required
                                                            onChange={(e) => handleChange(e)}
                                                            onBlur={(e) => handleRequiredCheck(e, 'VALIDATION_MandatoryBillAddrCity')}
                                                        />
                                                        <FormFeedback>
                                                            <span id="billAddrCity_LABEL"></span>
                                                        </FormFeedback>
                                                    </FormGroup>
                                                </div>

                                                {countries && Object.keys(countries).length !== 0 &&
                                                    <div className="c-form__input-group u-component-spacing">
                                                        <Translation ID="BillAddrCountry" required htmlFor='billAddrCountry' />
                                                        <FormGroup className="position-relative">
                                                            <Select2
                                                                id='billAddrCountry'
                                                                options={countries}
                                                                defaultValue={paymentDetails.billAddrCountry}
                                                                required
                                                                change={(e) => handleSelect2Change(e)}
                                                            />
                                                            <FormFeedback>
                                                                <span id="billAddrCountry_LABEL"></span>
                                                            </FormFeedback>
                                                        </FormGroup>
                                                    </div>
                                                }

                                                <div className="c-form__input-group u-component-spacing">
                                                    <Translation ID="BillAddrPostCode" required htmlFor='billAddrPostCode' />
                                                    <FormGroup className="position-relative">
                                                        <input name="billAddrPostCode" id="billAddrPostCode" type="text" defaultValue={paymentDetails.billAddrPostCode} className='c-form__input' required
                                                            onChange={(e) => handleChange(e)}
                                                            onBlur={(e) => handleRequiredCheck(e, 'VALIDATION_MandatoryBillAddrPostCode')}
                                                        />
                                                        <FormFeedback>
                                                            <span id="billAddrPostCode_LABEL"></span>
                                                        </FormFeedback>
                                                    </FormGroup>
                                                </div>

                                                <div className="c-form__input-group u-component-spacing">
                                                    <Translation ID="CompanyEmail" required htmlFor='companyEmail' />
                                                    <FormGroup className="position-relative">
                                                        <input name="companyEmail" id="companyEmail" type="text" defaultValue={paymentDetails.companyEmail} className='c-form__input form-control' required
                                                            onChange={(e) => { handleChange(e); checkEmailIsValid(e.target.id, e.target.value, 'VALIDATION_EmailComplexity') }}
                                                            onBlur={(e) => checkEmailIsValid(e.target.id, e.target.value, 'VALIDATION_MandatoryEmail')} />
                                                        <FormFeedback>
                                                            <span id="companyEmail_LABEL"></span>
                                                        </FormFeedback>
                                                    </FormGroup>
                                                </div></Col>
                                            <Col md={6}>
                                                <h2>Card Details</h2>

                                                <div className="c-form__input-group u-component-spacing">
                                                    <Translation ID="CardholderName" htmlFor='cardholderName' />
                                                    <FormGroup className="position-relative">
                                                        <input name="cardholderName" id="cardholderName" type="text" defaultValue='' className='c-form__input' autoComplete="off" required
                                                            onChange={(e) => handleCardholderNameSize(e, 2, 45)}
                                                            onBlur={(e) => handleRequiredCheck(e, 'VALIDATION_MandatoryCardholderName')} />
                                                        <FormFeedback>
                                                            <span id="cardholderName_LABEL"></span>
                                                        </FormFeedback>
                                                    </FormGroup>
                                                </div>

                                                <div className="c-form__input-group u-component-spacing">
                                                    <Translation ID="CardNumber" htmlFor='cardNumber' />
                                                    <FormGroup className="position-relative ">
                                                        <input name="cardNumber" id="cardNumber" type="tel" defaultValue='' className='c-form__input card-input-with-icon' required
                                                            onChange={(e) => handleCardNumberValidation(e)}
                                                            onBlur={(e) => handleRequiredCheck(e, 'VALIDATION_MandatoryCardNumber')}
                                                        />
                                                        {paymentGateway && paymentGateway.supportedCards && Object.keys(paymentGateway.supportedCards).length > 0 &&
                                                            <CardTypeIndicator supportedCards={paymentGateway.supportedCards} cardType={detectedCardType} />
                                                        }
                                                        <FormFeedback>
                                                            <span id="cardNumber_LABEL"></span>
                                                        </FormFeedback>
                                                    </FormGroup>
                                                </div>

                                                <div className="c-form__input-group u-component-spacing">
                                                    <Translation ID="CardExpiryDate" htmlFor='cardExpiryDateMM' />
                                                    <Row className="position-relative form-group">
                                                        <Col md={6} style={{ padding: "0px" }}>
                                                            <FormGroup >
                                                                <label htmlFor="cardExpiryDateMM" className="c-form__label">MM</label>
                                                                <input name="cardExpiryDateMM" id="cardExpiryDateMM" type="tel" defaultValue='' className='c-form__input' style={{ width: "75%" }} required
                                                                    onChange={(e) => handleMonthValidation(e)}
                                                                    onBlur={(e) => handleRequiredCheck(e, 'VALIDATION_MandatoryCardExpiryDateMM')}
                                                                />
                                                                <FormFeedback>
                                                                    <span id="cardExpiryDateMM_LABEL"></span>
                                                                </FormFeedback>
                                                            </FormGroup>
                                                        </Col>
                                                        <Col md={6}>
                                                            <FormGroup >
                                                                <label htmlFor="cardExpiryDateYY" className="c-form__label">YY</label>
                                                                <input name="cardExpiryDateYY" id="cardExpiryDateYY" type="tel" defaultValue='' className='c-form__input' pattern="[0-9]{2}" style={{ width: "75%" }} required
                                                                    onChange={(e) => handleYearValidation(e)}
                                                                    onBlur={(e) => handleRequiredCheck(e, 'VALIDATION_MandatoryCardExpiryDateYY')}
                                                                />
                                                                <FormFeedback>
                                                                    <span id="cardExpiryDateYY_LABEL"></span>
                                                                </FormFeedback>
                                                            </FormGroup>
                                                        </Col>
                                                    </Row>
                                                </div>

                                                <div className="c-form__input-group u-component-spacing">
                                                    <Translation ID="CardCVC" htmlFor='cvc' />
                                                    <FormGroup className="position-relative">
                                                        <input name="cvc" id="cvc" type="tel" defaultValue='' className='c-form__input' required
                                                            onChange={(e) => handleCVC(e)}
                                                            onBlur={(e) => handleRequiredCheck(e, 'VALIDATION_MandatoryCardCVC')}
                                                        />
                                                        <FormFeedback>
                                                            <span id="cvc_LABEL"></span>
                                                        </FormFeedback>
                                                    </FormGroup>
                                                </div>

                                                <hr />

                                                <div className="c-form__input-group u-component-spacing">
                                                    <label className="c-form__label">Sub-Total</label>
                                                    <label style={{ flexBasis: "75%", position: "relative !important" }}>&pound;{priceExcVat.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</label>
                                                </div>
                                                <div className="c-form__input-group u-component-spacing">
                                                    <label className="c-form__label">VAT (20%)</label>
                                                    <label style={{ flexBasis: "75%", position: "relative !important" }}>&pound;{(priceExcVat * 0.2).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</label>
                                                </div>
                                                <div className="c-form__input-group u-component-spacing">
                                                    <label className="c-form__label">TOTAL</label>
                                                    <label style={{ flexBasis: "75%", position: "relative !important" }}>&pound;{(priceExcVat + (priceExcVat * 0.2)).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</label>
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>

                                    <div className="u-flex-end u-component-spacing--2x">
                                        <button className="js-change-panel c-cta" onClick={() => handleMakePayment()}>
                                            Make Payment
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </ModalBody>
        </Modal>
    )
}

export default PaymentDetails_Modal;